import { Grid } from '../../../../../Common/Grid'
import { Button, Input } from '@fullscreendigital/carrefour-dex-fe'
import { TitleHeader } from '../../../../Common/TitleHeader'
import { useEffect, useMemo, useState } from "react"
import { useFormik } from 'formik'
import { useQuery } from 'react-query'
import { CircularProgress } from '@mui/material'
import { useNotification } from '../../../../../../../hooks'
import {
  makeCoreGetRequest,
  makeCorePostRequest,
  makeCorePutRequest
} from "../../../../../../../utils/api"
import { formatErrorMessage } from '../../../../../../../utils/formatting'
import { useLocation, useNavigate } from "react-router-dom"
import { URLS } from '../../../../constants'
import { getValidationSchema, initialValues } from './helpers'

type Supplier = {
  supplierCode: number | string
  supplierName: string
}

type DsReturnTruck = {
  id: number
  shopCode?: string
  supplierCode: number | string
  supplierName: string
  truckNumber: string
  driverName: string
}

const DsReturnTruckStep1 = () => {
  const { showNotification } = useNotification()
  const navigate = useNavigate()
  const location = useLocation()

  const editTruckId = location?.state?.truckId
  const isEditing = !!editTruckId

  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false)
  const validationSchema = useMemo(() => getValidationSchema(), [])

  const fetchSuppliers = async (): Promise<Supplier[]> => {
    const resp = await makeCoreGetRequest('/shop-reception-mobile/ds-return-truck/ds-suppliers-list', null)
    return resp as Supplier[]
  }

  const {
    data: supplierList,
    isLoading: isLoadingSuppliers,
    isError: isSuppliersError
  } = useQuery(
    'supplierList',
    fetchSuppliers,
    {
      onError: error => {
        showNotification({
          message: `Error while fetching suppliers. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const { data: shopCode, isLoading: isLoadingShopCode } = useQuery(
    'shopCode',
    async () => {
      const shopList = await makeCoreGetRequest('/shop-reception-mobile/home/user-shops', null)

      const storedShopCode = localStorage.getItem('selectedStoreCode')
      if (storedShopCode) return storedShopCode

      if (shopList.length > 0) return shopList[0].shopCode
    },
    {
      onError: error => {
        showNotification({
          message: `Error while fetching supports. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const { data: truckDetails } = useQuery<DsReturnTruck, Error>(
    ['dsReturnTruckDetails', editTruckId],
    async () => {
      const resp = await makeCoreGetRequest(`/shop-reception-mobile/ds-return-truck/${editTruckId}`, null)
      return resp as DsReturnTruck
    },
    {
      enabled: isEditing,
      staleTime: 0,
      refetchOnMount: 'always',
      onError: error => {
        showNotification({
          message: `Nu am putut încărca detaliile camionului. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const formik = useFormik({
    enableReinitialize: false,
    validateOnChange: false,
    initialValues,
    validationSchema,
    onSubmit: async values => {
      try {
        setIsSubmittingRequest(true)

        if (isEditing) {
          await makeCorePutRequest(
            `/shop-reception-mobile/ds-return-truck/update-ds-return-truck/${editTruckId}`,
            {
              truckNumber: values.truckNumber,
              driverName: values.driverName
            }
          )

          showNotification({
            message: 'Camion actualizat cu succes.',
            type: 'success'
          })

          navigate(URLS.AUTH_SUPPLIERS_RETURN_STEP_2, {
            state: { id: editTruckId, supplierCode: values.supplierCode }
          })

          return
        }

        const body = {
          shopCode,
          supplierCode: values.supplierCode,
          supplierName: values.supplierName,
          truckNumber: values.truckNumber,
          driverName: values.driverName
        }

        const resp = await makeCorePostRequest('/shop-reception-mobile/ds-return-truck/save-new-ds-return-truck', body)

        showNotification({
          message: 'Camion înregistrat cu succes.',
          type: 'success'
        })

        const { id, supplierCode } = resp
        navigate(URLS.AUTH_SUPPLIERS_RETURN_STEP_2, { state: { id, supplierCode } })
      } catch (error) {
        showNotification({
          message: `Error while submitting. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      } finally {
        setIsSubmittingRequest(false)
      }
    }
  })

  useEffect(() => {
    if (!isEditing) return
    if (!truckDetails) return
    if (!supplierList) return

    if (formik.dirty) return

    const selected = supplierList.find(
      s => String(s.supplierCode) === String(truckDetails.supplierCode)
    )

    formik.resetForm({
      values: {
        supplierCode: truckDetails.supplierCode ?? null,
        supplierName: selected?.supplierName || truckDetails.supplierName || '',
        truckNumber: truckDetails.truckNumber?.toUpperCase() || '',
        driverName: truckDetails.driverName?.toUpperCase() || ''
      }
    })
  }, [isEditing, truckDetails, supplierList, formik.dirty])

  if (isLoadingSuppliers || isLoadingShopCode) {
    return <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
  }

  const supplierSelectValues =
    supplierList?.map(s => ({
      value: s.supplierCode,
      label: `${s.supplierCode} - ${s.supplierName}`
    })) || []

  const isContinueDisabled =
    isSubmittingRequest ||
    !formik.values.supplierCode ||
    !formik.values.truckNumber ||
    !formik.values.driverName

  return (
    <Grid
      gridContainerStyle={{ marginTop: '20px' }}
      maxWidth="xs"
      items={[
        <TitleHeader title={isEditing ? "MODIFICĂ CAMION" : "ÎNREGISTREAZĂ CAMION"} />,

        <Input
          type="select"
          label="Furnizor"
          variant="outlined"
          width="100%"
          value={formik.values.supplierCode}
          values={supplierSelectValues}
          onChange={event => {
            const selectedCode = (event.target as HTMLInputElement).value

            const selectedSupplier = supplierList?.find(
              s => String(s.supplierCode) === String(selectedCode)
            )

            formik.setFieldValue('supplierCode', selectedCode)
            formik.setFieldValue('supplierName', selectedSupplier?.supplierName || '')
          }}
          error={!!formik.errors.supplierCode || !!formik.errors.supplierName}
          helperText={
            (formik.errors.supplierCode as string) ||
            (formik.errors.supplierName as string)
          }
          disabled={isEditing || isSuppliersError || supplierSelectValues.length === 0}
        />,

        <Input
          type="text"
          label="Număr înmatriculare"
          variant="standard"
          width="100%"
          value={formik.values.truckNumber}
          onChange={event =>
            formik.setFieldValue('truckNumber', (event.target as HTMLInputElement).value?.toUpperCase())
          }
          error={!!formik.errors.truckNumber}
          helperText={formik.errors.truckNumber as string}
        />,

        <Input
          type="text"
          label="Șofer"
          variant="standard"
          width="100%"
          value={formik.values.driverName}
          onChange={event =>
            formik.setFieldValue('driverName', (event.target as HTMLInputElement).value?.toUpperCase())
          }
          error={!!formik.errors.driverName}
          helperText={formik.errors.driverName as string}
        />,

        <Button
          type="button"
          variant="contained"
          label={isSubmittingRequest ? 'SE ÎNCARCĂ...' : 'PASUL URMĂTOR'}
          onClick={() => formik.handleSubmit()}
          style={{ width: '100%' }}
          disabled={isContinueDisabled}
        />
      ]}
      itemsPerRow={1}
    />
  )
}

export default DsReturnTruckStep1
